import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { ShoppingBag, User, Heart, Menu, X, Search, LogOut, Settings, Package } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';

export default function Navbar() {
  const { user, logout, isAdmin } = useAuth();
  const { cart, setOpen } = useCart();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenu, setUserMenu] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', fn);
    return () => window.removeEventListener('scroll', fn);
  }, []);

  useEffect(() => { setMobileOpen(false); setUserMenu(false); }, [location]);

  const handleLogout = () => { logout(); navigate('/'); };

  return (
    <>
      <nav className={`hfn-nav${scrolled ? ' scrolled' : ''}`}>
        <div className="nav-inner">
          {/* Logo */}
          <Link to="/" className="nav-logo">
            <span className="logo-green">HairFix</span>
            <span className="logo-gold"> Naturals</span>
          </Link>

          {/* Desktop links */}
          <ul className="nav-links">
            <li><Link to="/shop">Produktet</Link></li>
            <li><Link to="/blog">Blog</Link></li>
            <li><Link to="/shop?featured=true">Ofertat</Link></li>
            {isAdmin && <li><Link to="/admin" style={{ color: 'var(--gold)' }}>Admin</Link></li>}
          </ul>

          {/* Actions */}
          <div className="nav-actions">
            <button className="nav-icon-btn" onClick={() => navigate('/shop')}>
              <Search size={20} />
            </button>

            {user && (
              <button className="nav-icon-btn" onClick={() => navigate('/profile')}>
                <Heart size={20} />
              </button>
            )}

            <button className="nav-icon-btn cart-btn" onClick={() => setOpen(true)}>
              <ShoppingBag size={20} />
              {cart.item_count > 0 && <span className="cart-badge">{cart.item_count}</span>}
            </button>

            {user ? (
              <div className="user-menu-wrap">
                <button className="nav-icon-btn user-btn" onClick={() => setUserMenu(!userMenu)}>
                  <div className="user-avatar">{user.name?.[0]?.toUpperCase()}</div>
                </button>
                {userMenu && (
                  <div className="user-dropdown">
                    <div className="user-dropdown-header">
                      <div className="user-dropdown-name">{user.name}</div>
                      <div className="user-dropdown-email">{user.email}</div>
                    </div>
                    <div className="dropdown-divider" />
                    <Link to="/profile" className="dropdown-item"><User size={15} /> Profili im</Link>
                    <Link to="/orders" className="dropdown-item"><Package size={15} /> Porositë</Link>
                    {isAdmin && <Link to="/admin" className="dropdown-item" style={{ color: 'var(--gold)' }}><Settings size={15} /> Admin Panel</Link>}
                    <div className="dropdown-divider" />
                    <button className="dropdown-item danger" onClick={handleLogout}><LogOut size={15} /> Dil</button>
                  </div>
                )}
              </div>
            ) : (
              <Link to="/login" className="btn btn-primary btn-sm">Kyçu</Link>
            )}

            <button className="nav-icon-btn mobile-menu-btn" onClick={() => setMobileOpen(!mobileOpen)}>
              {mobileOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="mobile-menu">
          <Link to="/shop">🌿 Produktet</Link>
          <Link to="/blog">✍️ Blog</Link>
          <Link to="/cart">🛒 Shporta ({cart.item_count})</Link>
          {user ? (
            <>
              <Link to="/profile">👤 Profili</Link>
              <Link to="/orders">📦 Porositë</Link>
              {isAdmin && <Link to="/admin">⚙️ Admin</Link>}
              <button onClick={handleLogout}>🚪 Dil</button>
            </>
          ) : (
            <>
              <Link to="/login">Kyçu</Link>
              <Link to="/register">Regjistrohu</Link>
            </>
          )}
        </div>
      )}

      <style>{`
        .hfn-nav {
          position: fixed; top: 0; left: 0; right: 0; z-index: 1000;
          background: rgba(248,244,238,0.9); backdrop-filter: blur(16px);
          border-bottom: 1px solid transparent;
          transition: all 0.3s ease;
        }
        .hfn-nav.scrolled {
          border-bottom-color: var(--cream-dark);
          box-shadow: 0 2px 20px rgba(26,58,42,.08);
        }
        .nav-inner {
          max-width: 1200px; margin: 0 auto; padding: 0 24px;
          height: 68px; display: flex; align-items: center; justify-content: space-between; gap: 24px;
        }
        .nav-logo {
          font-family: 'Playfair Display', serif; font-size: 1.25rem; font-weight: 700;
          white-space: nowrap;
        }
        .logo-green { color: var(--green-deep); }
        .logo-gold { color: var(--gold); font-style: italic; }
        .nav-links {
          display: flex; gap: 28px; list-style: none; flex: 1; justify-content: center;
        }
        .nav-links a {
          font-size: 0.85rem; font-weight: 500; letter-spacing: 0.04em; text-transform: uppercase;
          color: var(--text-mid); transition: color 0.2s;
        }
        .nav-links a:hover { color: var(--green-deep); }
        .nav-actions { display: flex; align-items: center; gap: 6px; }
        .nav-icon-btn {
          width: 40px; height: 40px; border-radius: 50%;
          background: transparent; border: none; cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          color: var(--text-mid); transition: all 0.2s; position: relative;
        }
        .nav-icon-btn:hover { background: var(--cream-dark); color: var(--green-deep); }
        .cart-badge {
          position: absolute; top: 4px; right: 4px;
          width: 18px; height: 18px; border-radius: 50%;
          background: var(--green-deep); color: white;
          font-size: 0.65rem; font-weight: 700;
          display: flex; align-items: center; justify-content: center;
        }
        .user-avatar {
          width: 32px; height: 32px; border-radius: 50%;
          background: var(--green-deep); color: white;
          display: flex; align-items: center; justify-content: center;
          font-size: 0.85rem; font-weight: 700;
        }
        .user-menu-wrap { position: relative; }
        .user-dropdown {
          position: absolute; top: calc(100% + 8px); right: 0;
          background: white; border-radius: var(--radius-lg);
          box-shadow: var(--shadow-lg); min-width: 200px;
          overflow: hidden; border: 1px solid var(--cream-dark);
          animation: dropIn 0.15s ease;
        }
        @keyframes dropIn { from { opacity:0; transform:translateY(-8px); } to { opacity:1; transform:translateY(0); } }
        .user-dropdown-header { padding: 14px 16px; }
        .user-dropdown-name { font-weight: 600; font-size: 0.9rem; }
        .user-dropdown-email { font-size: 0.78rem; color: var(--text-light); }
        .dropdown-divider { height: 1px; background: var(--cream-dark); }
        .dropdown-item {
          display: flex; align-items: center; gap: 10px;
          padding: 11px 16px; font-size: 0.85rem; color: var(--text-mid);
          transition: background 0.15s; cursor: pointer; border: none; background: none;
          width: 100%; text-align: left;
        }
        .dropdown-item:hover { background: var(--cream); color: var(--green-deep); }
        .dropdown-item.danger { color: var(--danger); }
        .dropdown-item.danger:hover { background: var(--danger-soft); }
        .mobile-menu-btn { display: none; }
        .mobile-menu {
          display: none; position: fixed; top: 68px; left: 0; right: 0;
          background: var(--cream); padding: 20px; z-index: 999;
          box-shadow: var(--shadow-lg); border-top: 1px solid var(--cream-dark);
          flex-direction: column; gap: 4px;
        }
        .mobile-menu a, .mobile-menu button {
          display: block; padding: 14px 16px; font-size: 1rem; font-weight: 500;
          color: var(--text-dark); border-radius: var(--radius-md); border: none;
          background: none; cursor: pointer; text-align: left; width: 100%;
        }
        .mobile-menu a:hover, .mobile-menu button:hover { background: var(--green-soft); }
        @media (max-width: 768px) {
          .nav-links { display: none; }
          .mobile-menu-btn { display: flex !important; }
          .mobile-menu { display: flex; }
          .user-btn { display: none; }
        }
      `}</style>
    </>
  );
}
